/**
 * Shown in the Leave confirm (Settings → members): the keys the person owns in
 * this org. They stay there after they leave — unowned, and still working — so
 * each says who might have it and how to revoke it where it was issued (plan
 * 2026-10-17_KEYBOOK_REVOKE, decision 3). Nothing is shown when they own none,
 * or when the keybook can't answer (the leave itself still asks).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { keybookApi } from '../client/api.js'
import { KeyRevokeSummary } from './KeyRevokeNotice.js'

type Owned = Awaited<ReturnType<typeof keybookApi.getOwnedKeysForLeaving>>

export function LeavingKeysNotice({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('keybook')
  const [owned, setOwned] = useState<Owned | null>(null)
  useEffect(() => {
    let alive = true
    keybookApi.getOwnedKeysForLeaving(tenantId).then(x => { if (alive) setOwned(x) }).catch(() => {})
    return () => { alive = false }
  }, [tenantId])
  if (!owned || owned.keys.length === 0) return null
  const total = owned.keys.length + owned.more
  return (
    <div className="space-y-2 text-left" data-testid="keybook-leaving-notice">
      <p className="font-medium">{t('revoke.leaving.title', { count: total })}</p>
      <p className="text-xs themed-text-muted">{t('revoke.leaving.body')}</p>
      <ul className="space-y-2 max-h-64 overflow-y-auto">
        {owned.keys.map(k => (
          <li key={k.id} className="kbg-well px-3 py-2 space-y-1" data-testid="keybook-leaving-key">
            <p className="text-xs font-semibold themed-text">{k.label}</p>
            <KeyRevokeSummary credentialType={k.credentialType} provider={k.provider} exposure={k.exposure} />
          </li>
        ))}
      </ul>
      {owned.more > 0 && <p className="text-xs themed-text-muted">{t('revoke.leaving.more', { count: owned.more })}</p>}
    </div>
  )
}
